import { useQuery } from '@tanstack/react-query'
import React, { useCallback } from 'react'
import useLocalStorageState from 'use-local-storage-state'
import { createFetcher } from '../../libs/next/api/jsonApiHandler'
import type { API } from '../../pages/api/loaders/[handler]'
import type { API as DogAPI } from '../../pages/api/user/[handler]'
import { useLocalStorageDogInfo } from '../components/dogForm/DogInfoForm'
import type { DogInfoSchema } from '../components/dogForm/DogInfoSchema'
import { useAuth } from './UserContext'
import { dogResultToDogInfo } from './dogResultToDogInfo'

const dogApi = createFetcher<DogAPI>('/api/user/[handler]')
const api = createFetcher<API>('/api/loaders/[handler]')

export function useActiveDog() {
	const auth = useAuth()

	const user = auth.status === 'signedIn' ? auth.user : null

	const [localStorageDogInfo, setLocalStorageDogInfo, clearLocalStorageDogInfo] = useLocalStorageDogInfo()

	const [activeDogId, setActiveDogId] = useLocalStorageState(`${user?.id}/activeDogId`)

	const dogEntity = React.useMemo(() => {
		if (user) {
			const mostRecentDog = user.dogs?.sort(
				(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			)[0]

			return activeDogId ? user.dogs?.find((dog) => dog.id === activeDogId) : (mostRecentDog ?? null)
		}
		return null
	}, [activeDogId, user])

	const dogInfo = React.useMemo(() => {
		return dogEntity ? dogResultToDogInfo(dogEntity) : null
	}, [dogEntity])

	const setActiveDog = useCallback(
		(dog: null | string | DogInfoSchema) => {
			if (!dog) {
				setActiveDogId(null)
				clearLocalStorageDogInfo()
			} else {
				if (typeof dog === 'string') {
					setActiveDogId(dog)
				} else {
					setLocalStorageDogInfo(dog)
				}
			}
		},
		[clearLocalStorageDogInfo, setActiveDogId, setLocalStorageDogInfo],
	)

	const saveActiveDog = useCallback(
		(dog: DogInfoSchema, userId = user?.id) => {
			if (userId) {
				if (dog.id) {
					if (localStorageDogInfo) {
						dogApi('createDog', { dog }).then(() => {
							auth.me()
						})
						clearLocalStorageDogInfo()
					} else {
						dogApi('updateDog', { dog }).then(() => {
							auth.me()
						})
					}
				} else {
					dogApi('createDog', { dog }).then(() => {
						auth.me()
					})
					clearLocalStorageDogInfo()
				}
			} else {
				setLocalStorageDogInfo(dog)
			}
		},
		[auth, clearLocalStorageDogInfo, localStorageDogInfo, setLocalStorageDogInfo, user?.id],
	)

	return {
		dog: auth.status === 'signedIn' ? dogInfo : auth.status === 'signedOut' ? localStorageDogInfo : null,
		isLoading: auth.status === 'loading',
		setActiveDog,
		saveActiveDog,
	}
}

export function useDER() {
	const { dog } = useActiveDog()

	const dogInfo = dog?.id ?? dog
	return useQuery({
		queryKey: ['getDailyEnergyRequirementOfDog', dogInfo],
		queryFn: async () => {
			return dogInfo
				? api('getDailyEnergyRequirementOfDog', {
						dogInfo,
					})
				: null
		},
		enabled: Boolean(dogInfo),
	})
}

export function useDERCalculator() {
	const der = useDER()

	const dataDer = der.data?.dailyEnergyRequirement

	return {
		to:
			(diff = 0) =>
			(value: number | null | undefined) => {
				if (diff > 0) {
					return diff + Number(value) * (dataDer ?? 0)
				}
				return Number(value) * (dataDer ?? 0)
			},
		from:
			(diff = 0) =>
			(value: number | null | undefined) => {
				if (diff > 0) {
					return (Number(value) - diff) / (dataDer ?? 0)
				}
				return Number(value) / (dataDer ?? 0)
			},
	}
}
